import { useMemo, useState } from "react";
import { m } from "@/paraglide/messages";
import { countText, isHaiku } from "../lib/syllables";

const HAIKU_PATTERN = [5, 7, 5];

function Stat({
  label,
  value,
  primary = false,
}: {
  label: string;
  value: number;
  primary?: boolean;
}) {
  return (
    <div className="pct-panel rounded-xl py-4 text-center">
      <div
        className={`font-display text-3xl font-bold tabular-nums ${primary ? "pct-result" : ""}`}
      >
        {value}
      </div>
      <div className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

export function SyllableCounter({ haiku = false }: { haiku?: boolean }) {
  const [text, setText] = useState("");
  const result = useMemo(() => countText(text), [text]);
  const hasContent = result.lines.some((l) => l.words > 0);
  const haikuOk = haiku && isHaiku(text);

  return (
    <div className="pct-card space-y-6 rounded-2xl p-6 sm:p-8">
      <textarea
        aria-label={m.syl_input_label()}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={haiku ? 4 : 8}
        spellCheck={false}
        placeholder={
          haiku ? m.syl_haiku_placeholder() : m.syl_input_placeholder()
        }
        className="w-full resize-y rounded-md border-black/15 bg-white p-3 text-base text-neutral-900 shadow-xs outline-none placeholder:text-neutral-500 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:border-black/15 dark:bg-white dark:text-neutral-900"
      />

      <div className="grid grid-cols-3 gap-3" aria-live="polite">
        <Stat label={m.syl_stat_syllables()} value={result.syllables} primary />
        <Stat label={m.syl_stat_words()} value={result.words} />
        <Stat label={m.syl_stat_characters()} value={result.characters} />
      </div>

      {hasContent && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground">
            {m.syl_per_line()}
          </h3>
          <ul className="space-y-1">
            {result.lines.map((line, i) => {
              const target = haiku && i < 3 ? HAIKU_PATTERN[i] : null;
              const ok =
                target == null || line.words === 0 || line.syllables === target;
              return (
                <li
                  key={`${i}-${line.text}`}
                  className="flex items-center justify-between gap-3 rounded-md border border-border/50 px-3 py-1.5 text-sm"
                >
                  <span className="truncate font-mono text-muted-foreground">
                    {line.text || " "}
                  </span>
                  <span
                    className={`shrink-0 tabular-nums ${ok ? "text-foreground" : "text-destructive"}`}
                  >
                    {line.syllables}
                    {target != null ? ` / ${target}` : ""}
                  </span>
                </li>
              );
            })}
          </ul>

          {haiku && (
            <p
              className={`text-sm font-medium ${haikuOk ? "text-primary" : "text-muted-foreground"}`}
            >
              {haikuOk ? m.syl_haiku_valid() : m.syl_haiku_invalid()}
            </p>
          )}
        </div>
      )}

      <p className="text-xs text-muted-foreground">{m.syl_estimate_note()}</p>
    </div>
  );
}
